import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import AddAdministrationRecordButton from './mar/AddAdministrationRecordButton';
import AdministrationRecordsTable from './mar/AdministrationRecordsTable';
import QuickDiscontinueButton from './QuickDiscontinueButton';
import { contradictionAnchor } from '../oasis/contradictions/contradictionNavigation';
import type {
  MarMedicationResponse,
  PatientMedicationListItem,
  PatientMedicationResponse,
} from '../../services/api';
import {
  getMedicationDisplayName,
  getMedicationDosageDisplay,
  isActuallyDiscontinued,
} from './medicationUtils';
import { getMedicationStatusFlags } from './medicationStatusFlags';
// Dates are numeric here, not long-form: this is a compact table, and the
// imported Kinnser rows share these two columns. One column, one format.
import {
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';

interface MedicationRowMarContext {
  entries: MarMedicationResponse[];
  /** Whether an administration can be recorded right now. */
  canRecord: boolean;
  /** Explanation shown when recording is blocked (no visit / signed visit). */
  blockedReason: string;
  onAdd: () => void;
  onEdit: (entry: MarMedicationResponse) => void;
  onDelete: (entry: MarMedicationResponse) => void;
}

interface MedicationTableRowProps {
  medication: PatientMedicationListItem;
  onEdit: (medication: PatientMedicationResponse) => void;
  onDelete: (medication: PatientMedicationResponse) => void;
  onDiscontinue: (medicationId: string, date: Date) => Promise<void>;
  /** No edit, delete or discontinue (External EMR chart lock). */
  readOnly?: boolean;
  selection?: {
    selected: boolean;
    eligible: boolean;
    reason?: string;
    onToggle: (checked: boolean) => void;
  };
  mar?: MedicationRowMarContext;
  /** 'row' renders a desktop table row; 'card' renders a mobile stacked card. */
  variant?: 'row' | 'card';
}

const MedicationTableRow: React.FC<MedicationTableRowProps> = ({
  medication: med,
  onEdit,
  onDelete,
  onDiscontinue,
  readOnly = false,
  selection,
  mar,
  variant = 'row',
}) => {
  // null = follow the default (expanded when there are records); once the user
  // toggles, their choice sticks. This also handles MAR data arriving after the
  // row first mounts.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = parseISOToLocalDate(med.start_date)! > today;
  const isDiscontinued = isActuallyDiscontinued(med);
  const isMuted = isFuture || isDiscontinued;

  // Row-level contradiction jump target. The N0415 / M2030 rules
  // resolve to medication rows via medication_concepts.json, so both
  // responsive variants carry the anchor — MedicationsTable renders one or the
  // other depending on viewport.
  const contradictionAnchorProps = contradictionAnchor(
    'medication_list',
    med.id
  );

  const dosageDisplay = getMedicationDosageDisplay(med);

  const administrationCount = mar?.entries.length ?? 0;
  const expanded = userExpanded ?? administrationCount > 0;
  const showExpandedRow = Boolean(mar) && expanded && administrationCount > 0;
  // Total columns for the expanded row's full-width cell.
  const columnCount =
    5 /* status, start, discontinue, medication, actions */ +
    (mar ? 1 : 0) +
    (selection ? 1 : 0);

  // Shared rendering pieces reused by both the desktop row and the mobile card
  // so handlers and derived state stay identical across variants.
  //
  // Every row says its status, including the ordinary one. Blank is not a
  // status — and since imported Kinnser lines share this column and always
  // carry a word, a blank cell beside them reads as data that failed to load
  // rather than as a medication the patient is simply taking.
  //
  // This badge answers only where the medication sits in its lifecycle. The
  // recency flags a clinician ticks are a separate question, answered by the
  // chips below, so that a discontinued or future medication no longer hides
  // the flags it carries.
  const statusBadge = isFuture ? (
    <Badge variant='secondary' className='text-xs w-fit whitespace-nowrap'>
      Future
    </Badge>
  ) : isDiscontinued ? (
    <Badge variant='secondary' className='text-xs w-fit whitespace-nowrap'>
      Discontinued
    </Badge>
  ) : (
    <Badge variant='outline' className='text-xs w-fit whitespace-nowrap'>
      Active
    </Badge>
  );

  // Longstanding / New / Changed, on the row itself. Reading them used to mean
  // opening the medication, which is a lot of clicking for three checkboxes a
  // clinician scans a whole list for. The codes are the printed record's own,
  // and the table decodes them once in a legend above itself.
  //
  // The chips are deliberately inert. A per-chip tooltip would not reach a
  // phone at all, since Radix suppresses tooltips for touch pointers, and
  // making each one focusable to fix that would add three tab stops per row to
  // reach wording the legend already gives. The full phrase is still here for
  // assistive technology, which reads it in place of the code.
  const statusFlags = getMedicationStatusFlags(med);
  const flagChips = statusFlags.length ? (
    <div className='flex flex-wrap items-center gap-1'>
      {statusFlags.map(flag => (
        <Badge
          key={flag.code}
          variant='outline'
          className='text-xs whitespace-nowrap'
        >
          <span aria-hidden='true'>{flag.code}</span>
          <span className='sr-only'>{flag.label}</span>
        </Badge>
      ))}
    </div>
  ) : null;

  const checkbox = selection ? (
    <Checkbox
      checkboxSize='sm'
      checked={selection.selected}
      disabled={!selection.eligible}
      onCheckedChange={checked => selection.onToggle(Boolean(checked))}
      aria-label={`Select ${getMedicationDisplayName(med)}`}
    />
  ) : null;

  const discontinueContent =
    selection || readOnly ? (
      med.discontinue_date ? (
        <span className='text-muted-foreground'>
          {formatDateToMMDDYYYY(med.discontinue_date)}
        </span>
      ) : (
        <span className='text-sm text-muted-foreground'>Active</span>
      )
    ) : med.discontinue_date ? (
      <span className='text-muted-foreground'>
        {formatDateToMMDDYYYY(med.discontinue_date)}
      </span>
    ) : (
      <QuickDiscontinueButton
        minDate={parseISOToLocalDate(med.start_date)!}
        onDiscontinue={date => onDiscontinue(med.id, date)}
      />
    );

  const medicationDetails = (
    <div>
      <div className='font-medium'>{getMedicationDisplayName(med)}</div>
      {(dosageDisplay || med.frequency_instructions) && (
        <div className='text-sm text-muted-foreground mt-0.5'>
          {dosageDisplay}
          {dosageDisplay && med.frequency_instructions && ' - '}
          {med.frequency_instructions}
        </div>
      )}
      {med.medication?.therapeutic_category && (
        <div className='text-sm text-muted-foreground mt-0.5'>
          {med.medication.therapeutic_category}
        </div>
      )}
      {selection?.reason && (
        <div className='text-xs text-muted-foreground mt-1'>
          {selection.reason}
        </div>
      )}
    </div>
  );

  const marControl = mar ? (
    administrationCount > 0 ? (
      <button
        type='button'
        onClick={() => setUserExpanded(!expanded)}
        aria-expanded={expanded}
        className='flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary'
      >
        {expanded ? (
          <ChevronDown className='h-4 w-4' />
        ) : (
          <ChevronRight className='h-4 w-4' />
        )}
        {administrationCount} Administration
        {administrationCount === 1 ? '' : 's'} this visit
      </button>
    ) : (
      <AddAdministrationRecordButton
        canRecord={mar.canRecord}
        blockedReason={mar.blockedReason}
        onAdd={mar.onAdd}
      />
    )
  ) : null;

  const actions =
    selection || readOnly ? null : (
      <div className='flex items-center gap-1'>
        <Button
          variant='ghost'
          size='sm'
          aria-label='Edit medication'
          onClick={() => onEdit(med)}
        >
          <Pencil className='h-4 w-4' />
        </Button>
        {med.has_administration_records ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex' tabIndex={0}>
                  <Button
                    variant='ghost'
                    size='sm'
                    aria-label='Delete medication'
                    disabled
                  >
                    <Trash2 className='h-4 w-4 text-muted-foreground' />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                This medication has administration records and can&apos;t be
                deleted. Discontinue it instead.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            variant='ghost'
            size='sm'
            aria-label='Delete medication'
            onClick={() => onDelete(med)}
          >
            <Trash2 className='h-4 w-4 text-destructive' />
          </Button>
        )}
      </div>
    );

  const administrationRecords =
    showExpandedRow && mar ? (
      <AdministrationRecordsTable
        entries={mar.entries}
        canRecord={mar.canRecord}
        blockedReason={mar.blockedReason}
        onAdd={mar.onAdd}
        onEdit={mar.onEdit}
        onDelete={mar.onDelete}
      />
    ) : null;

  if (variant === 'card') {
    return (
      <div
        {...contradictionAnchorProps}
        className={`rounded-md border p-3 space-y-3 ${
          isMuted ? 'opacity-60' : ''
        }`}
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='flex items-start gap-2 min-w-0'>
            {checkbox && <div className='pt-0.5'>{checkbox}</div>}
            {medicationDetails}
          </div>
          <div className='flex shrink-0 flex-col items-end gap-1'>
            {statusBadge}
            {flagChips}
          </div>
        </div>
        <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Start</span>
            <span>{formatDateToMMDDYYYY(med.start_date)}</span>
          </div>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Discontinue</span>
            <span>{discontinueContent}</span>
          </div>
        </div>
        {mar && (
          <div className='space-y-2'>
            {marControl}
            {administrationRecords}
          </div>
        )}
        {actions && (
          <div className='flex justify-end border-t pt-2'>{actions}</div>
        )}
      </div>
    );
  }

  return (
    <>
      <TableRow
        {...contradictionAnchorProps}
        className={isMuted ? 'opacity-60' : undefined}
      >
        {selection && <TableCell>{checkbox}</TableCell>}
        <TableCell>
          <div className='flex flex-col gap-1'>
            {statusBadge}
            {flagChips}
          </div>
        </TableCell>
        <TableCell className='text-sm'>
          {formatDateToMMDDYYYY(med.start_date)}
        </TableCell>
        <TableCell className='text-sm'>{discontinueContent}</TableCell>
        <TableCell>{medicationDetails}</TableCell>
        {mar && <TableCell className='align-top'>{marControl}</TableCell>}
        <TableCell>
          {selection ? (
            <span className='sr-only'>Medication selection mode</span>
          ) : (
            actions
          )}
        </TableCell>
      </TableRow>
      {showExpandedRow && mar && (
        <TableRow className='hover:bg-transparent'>
          <TableCell colSpan={columnCount} className='bg-muted/30 p-0'>
            {administrationRecords}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default MedicationTableRow;
